import React, { useLayoutEffect, useMemo } from 'react';

import Animated from 'react-native-reanimated';
import { initialWindowMetrics } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { StackHeaderOptions } from '@react-navigation/stack/src/types';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { selectScreenReaderEnabled } from 'store/config/selectors';
import { selectBackground } from 'store/config/selectors.layout';

import isFunction from 'utils/function';
import { isAndroid15, isWeb } from 'utils/responsiveness';

import AnimatedHeaderBackground from 'components/NavigationHeader/AnimatedHeaderBackground';
import RootNavigationHeader from 'components/NavigationHeader/RootNavigationHeader';
import SimpleNavigationHeaderLeft from 'components/NavigationHeader/SimpleNavigationHeader/Left';
import SimpleNavigationHeaderTitle from 'components/NavigationHeader/SimpleNavigationHeader/Title';

import useHeaderHeight from './useHeaderHeight';

type Params = {
    isRoot?: boolean;
    needsClose?: boolean;
    needsCast?: boolean;
    headerTransparent?: boolean;
    screenFocused?: boolean;
    showSideMenuOnBack?: boolean;
    onCloseButton?(): void;
    pageScrollPosition?: Animated.SharedValue<number>;
    headerLeft?: () => React.ReactNode;
    headerRight?: () => React.ReactNode;
    noInsetHeader?: boolean;
    disableFocusOnMount?: boolean;
};

const useHeaderConfig = ({
    isRoot,
    headerTransparent,
    screenFocused,
    pageScrollPosition,
    headerRight,
    headerLeft,
    noInsetHeader = false,
    disableFocusOnMount = false,
}: Params) => {
    const navigation =
        useNavigation<
            StackNavigationProp<
                RouteNavigationParams,
                keyof RouteNavigationParams
            >
        >();
    const route =
        useRoute<
            RouteProp<RouteNavigationParams, keyof RouteNavigationParams>
        >();

    const backgroundColor = useSelector(selectBackground);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);
    const headerHeight = useHeaderHeight(!noInsetHeader);

    const headerStyle = useMemo(
        () => ({
            height:
                headerHeight +
                (isAndroid15 &&
                !(route.name === SCREEN.DETAILS && screenReaderEnabled)
                    ? initialWindowMetrics?.insets.top || 0
                    : 0),
            shadowColor: 'transparent',
            backgroundColor,
        }),
        [backgroundColor, headerHeight, route.name, screenReaderEnabled],
    );

    useLayoutEffect(() => {
        if (isRoot) {
            navigation.setOptions({
                header: ({ progress }) => {
                    return (
                        <RootNavigationHeader
                            navigation={navigation}
                            style={headerStyle}
                            pageScrollPosition={pageScrollPosition}
                            progress={progress}
                            disableFocusOnMount={disableFocusOnMount}
                        />
                    );
                },
                headerStyle,
                headerTransparent,
                headerMode: screenReaderEnabled || isWeb ? 'float' : 'screen',
                ...(isFunction(headerLeft) && {
                    headerLeft: () => headerLeft(),
                }),
                ...(isFunction(headerRight) && {
                    headerRight: () => headerRight(),
                }),
                // Workaround for the issue when headerTransparent === true.
                // In this case react-navigation is positioning header absoultely
                // and this leads to the case when accessibility tools don't seem to be able to find the header.
                // In result header and all its content is not accessible.
                // https://github.com/react-navigation/react-navigation/issues/9905
                ...(!isWeb &&
                    screenFocused != null &&
                    screenReaderEnabled && { headerShown: screenFocused }),
            });
        } else if (headerTransparent && pageScrollPosition) {
            navigation.setOptions({
                headerBackground() {
                    return (
                        <AnimatedHeaderBackground
                            scrollPosition={pageScrollPosition}
                        />
                    );
                },
                ...(isFunction(headerLeft) && {
                    headerLeft: () => headerLeft(),
                }),
                ...(isFunction(headerRight) && {
                    headerRight: () => headerRight(),
                }),
                headerStyle,
                headerTransparent,
                title: '',

                // Workaround for the issue when headerTransparent === true.
                // In this case react-navigation is positioning header absoultely
                // and this leads to the case when accessibility tools don't seem to be able to find the header.
                // In result header and all its content is not accessible.
                // https://github.com/react-navigation/react-navigation/issues/9905
                ...(!isWeb &&
                    screenFocused != null &&
                    screenReaderEnabled && { headerShown: screenFocused }),
                ...(!isWeb && {
                    headerMode: screenReaderEnabled ? 'float' : 'screen',
                }),
            });
        } else if (
            route.name === SCREEN.PROFILE_AGE_RATING ||
            route.name === SCREEN.PROFILE_AVATARS
        ) {
            navigation.setOptions({
                headerStyle,
                headerTransparent,
                headerTitle: () => <SimpleNavigationHeaderTitle />,
                headerLeft: () => <SimpleNavigationHeaderLeft />,
                headerTitleAlign:
                    'left' as StackHeaderOptions['headerTitleAlign'],
            });
        } else {
            navigation.setOptions({
                ...(isFunction(headerLeft) && {
                    headerLeft: () => headerLeft(),
                }),
                ...(isFunction(headerRight) && {
                    headerRight: () => headerRight(),
                }),
                headerTitleContainerStyle: {
                    marginLeft: 3,
                },
                headerStyle,
                headerTransparent,
                title: '',
            });
        }
    }, [
        isRoot,
        screenFocused,
        headerTransparent,
        pageScrollPosition,
        headerRight,
        headerLeft,
        route,
        headerStyle,
        screenReaderEnabled,
        disableFocusOnMount,
    ]);
};

export default useHeaderConfig;
